import * as React from 'react';

import { useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import BackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import { NavLink } from 'react-router-dom';
import { useWindowDimensions, copyArrayWithIndexChanged } from '../Utils';

import { Breadcrumbs, Box, Button, Autocomplete, Stack, TextField, Typography, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { BreadCrumbProps } from "../subs/PageAlternate";
import { InterfaceAdmin, GetEmptyInterface } from './Administration/InterfaceAdmin';
import { TestcaseAdmin, GetEmptyTestCase } from './Administration/TestCaseAdmin';
import { PropertiesAdmin } from './Administration/InterfacePropertyAdmin';
import { Interface, InterfaceProperty } from '../types/Interface';
import { TestCase } from '../types/TestCase';
import { TestCombination, TestCombinationStatusStrings, getTestCombinationList, updateTestCombination } from '../types/TestCombinations';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../contexts/SnackBarContext';

// global theming ruins the back button, disable it here
const backTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                },
            },
        }
    }
});

export interface PAAPageProps {
    architectureview?: React.ReactNode,
    interfaceview?: React.ReactNode,
    testcaseview?: React.ReactNode,
    propertiesview?: React.ReactNode,
    backButton?: boolean,
    breadcrumbs?: BreadCrumbProps[]
}

const breadcrumbs: BreadCrumbProps[] = [
    { to: "/Administration", text: "Administration" },
    { to: "/DBFunctionPage", text: "Functions" },
];

export const ArchitectureAdmin = () => {
    const [selectedTestCombination, setSelectedTestCombination] = React.useState<TestCombination>({});
    const [selectedInterface, setSelectedInterface] = React.useState<Interface>(GetEmptyInterface());
    const [selectedIFaceProp, setSelectedIFaceProp] = React.useState<InterfaceProperty>(GetEmptyTestCase());
    const [selectedTestCase, setSelectedTestCase] = React.useState<TestCase>(GetEmptyTestCase());
    //   const userContext = React.useContext(UserContext);

    return <ArchitecturePage backButton
        architectureview={<ProductArchitectureAdmin title={'Select or create a Test Combination to manage'} selectedTestCombination={selectedTestCombination} setSelectedTestCombination={setSelectedTestCombination} />}
        interfaceview={<InterfaceAdmin selectedTestCombination={selectedTestCombination} selectedInterface={selectedInterface} setSelectedInterface={setSelectedInterface} />}
        testcaseview={<TestcaseAdmin selectedTestCombination={selectedTestCombination} selectedInterface={selectedInterface} selectedTestCase={selectedTestCase} setSelectedTestCase={setSelectedTestCase} />}
        propertiesview={<PropertiesAdmin selectedTestCombination={selectedTestCombination} selectedInterface={selectedInterface}
            selectedIFaceProp={selectedIFaceProp} setSelectedIFaceProp={setSelectedIFaceProp} />}
        breadcrumbs={breadcrumbs} />
}

const columns: GridColDef[] = [{
    field: 'status',
    headerName: 'Status',
    flex: 1,
    valueFormatter: (value?: number) => {
        if (value == null) {
            return '';
        }
        return TestCombinationStatusStrings[value];
    }
},
{
    field: 'combinationName',
    headerName: 'Name',
    flex: 2
},
{
    field: 'combinationProvider',
    headerName: 'Provider',
    flex: 3,
},
{
    field: 'combinationConsumer',
    headerName: 'Consumer',
    flex: 3
    },
    {
        field: 'id'
    }
];

const ProductArchitectureAdmin = ({ title, selectedTestCombination, setSelectedTestCombination }:
    { title: string, selectedTestCombination: TestCombination, setSelectedTestCombination: (value: React.SetStateAction<TestCombination>) => void }) => {
    const { showSnackbar } = useSnackbar();
    const [testCombinations, setTestCombinations] = React.useState<TestCombination[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchTestCombinations = async () => {
            const testCombinations = await getTestCombinationList(new AbortController());
            if (testCombinations.failed) {
                showUserFeedback(testCombinations.errorText);
                return;
            }
            setTestCombinations(testCombinations.result);
        }
        fetchTestCombinations();
    }, [showUserFeedback]);

    const saveHandler = async (testCombination: TestCombination) => {
        const updatedTestCombination = await updateTestCombination(testCombination, new AbortController());
        if (updatedTestCombination.failed) {
            showUserFeedback(updatedTestCombination.errorText);
            return;
        }
        const updatedTestCombinationLoc = updatedTestCombination.result;
        setSelectedTestCombination(updatedTestCombinationLoc);
        let testCombinationsLoc = copyArrayWithIndexChanged(testCombinations, testCombinations.findIndex(tc => tc.id === updatedTestCombinationLoc.id), updatedTestCombinationLoc);
        setTestCombinations(testCombinationsLoc);
    }

    const onSubmitHandler = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        saveHandler(selectedTestCombination);
    }

    const onSelectionChange = (sel: GridRowParams) => {
        const selectedTC = testCombinations.find(tc => tc.id === sel.id) as TestCombination;
        if (selectedTC.id === selectedTestCombination.id) {
            setSelectedTestCombination({}); // deselect if already selected
        }
        else setSelectedTestCombination(selectedTC);
    }

    return <React.Fragment>
        <Typography variant='h4'>{title}</Typography>
        <Box sx={{ height: '90%', width: '99%', mt: '20px' }}>
            <form id='testCombinationForm' onSubmit={onSubmitHandler}>
                <Grid container spacing={2} alignItems={'center'} >
                    <Grid size={12} paddingBottom={10}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel shrink htmlFor='combinationName'>{"Name"}</InputLabel>
                            <OutlinedInput notched name='combinationName' id='combinationName'
                                label={"Name"} value={selectedTestCombination?.combinationName}
                                onChange={(e) => setSelectedTestCombination({ ...selectedTestCombination, combinationName: e.target.value })}
                                />
                        </FormControl>
                    </Grid>
                    <Grid size={6} paddingBottom={10}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel shrink htmlFor='combinationProvider'>{"Provider"}</InputLabel>
                            <OutlinedInput notched name='combinationProvider' id='combinationProvider'
                                label={"Provider"} value={selectedTestCombination?.combinationProvider}
                                onChange={(e) => setSelectedTestCombination({ ...selectedTestCombination, combinationProvider: e.target.value })}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6} paddingBottom={10}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel shrink htmlFor='combinationConsumer'>{"Consumer"}</InputLabel>
                            <OutlinedInput notched name='combinationConsumer' id='combinationConsumer'
                                label={"Consumer"} value={selectedTestCombination?.combinationConsumer}
                                onChange={(e) => setSelectedTestCombination({ ...selectedTestCombination, combinationConsumer: e.target.value })}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={4}>
                        <FormControl required fullWidth>
                            <Autocomplete id='tcSelect' options={TestCombinationStatusStrings}
                                value={TestCombinationStatusStrings[(selectedTestCombination?.status) ?? 0]}
                                onChange={(e, value) => setSelectedTestCombination({ ...selectedTestCombination, status: TestCombinationStatusStrings.indexOf(value as string) })}
                                renderInput={(params) =>
                                    <TextField name='tcSelect' multiline
                                        {...params}
                                        label={"Status"}
                                        sx={{
                                            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                                borderColor: 'divider',
                                            },
                                        }}
                                    />
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={3} />
                    <Grid size={2}>
                        <Button fullWidth startIcon={<ClearIcon />}
                            onClick={() => {
                                setSelectedTestCombination({ combinationName: "", combinationConsumer: "", combinationProvider: "", id: 0, status: 0, guid:"" });
                            }}>
                            New
                        </Button>
                    </Grid>
                    <Grid size={1} />
                    <Grid size={2}>
                        <Button form='testCombinationForm' type='submit' fullWidth startIcon={<SaveIcon />}>Save</Button>
                    </Grid>
                </Grid>
            </form>
            <Box sx={{ width: '100%' }} marginTop={15}>
                <DataGrid
                    columns={columns}
                    rows={testCombinations}
                    columnVisibilityModel={{ id: false }}
                    onRowClick={onSelectionChange}
                    getRowClassName={(params) => params.row.id === selectedTestCombination.id ? 'selectRowClass': ''}
                    hideFooter
                    sx={{
                        "& .selectRowClass": {
                            color: 'primary.main'
                        },
                    }}
                />
            </Box>
        </Box>
    </React.Fragment>
}

const ArchitecturePage = ({ backButton, breadcrumbs, architectureview, interfaceview, testcaseview, propertiesview }: PAAPageProps) => {
    const navigate = useNavigate();
    const [focusedView, setFocusedView] = React.useState(1); //true = expanded
    const { height, width } = useWindowDimensions();

    const maxHeight = height - 180 + 'px';
    const sidePanelwidth = width * 0.4;
    const mainWidth = width - sidePanelwidth - 180 + 'px';
    const fullwidth = width - 230 + 'px'
    const spWidth = sidePanelwidth + 'px'

    const backButtonHandler = () => navigate(-1);
    const architectureViewToggleHandeler = () => setFocusedView(fv => fv === 1 ? 2:1);
    const interfaceViewToggleHandeler = () => setFocusedView(fv => fv === 2 ? 3 : 2);
    const testcaseViewToggleHandeler = () => setFocusedView(fv => fv === 3 ? 4 : 3);

    const visibleElement = { visibility: "visible" };
    const hiddenElement = { visibility: "hidden" };

    return <>
        <Stack direction="row" spacing={200}>
            <ThemeProvider theme={backTheme}>
                <Button startIcon={<BackIcon />} size="small" onClick={backButtonHandler} sx={backButton ? visibleElement : hiddenElement}>back</Button>
            </ThemeProvider>
            {breadcrumbs && <Breadcrumbs separator="|">
                {breadcrumbs.map((crumb, index) => (
                    <NavLink key={index} to={crumb.to}>{crumb.text}</NavLink>
                ))}
            </Breadcrumbs>}
        </Stack>

        <Stack direction="row" marginTop="10px" gap={10}>
            <Stack direction="row" minWidth={focusedView === 1 ? spWidth : "max-content"} marginTop="5" marginRight="5" minHeight={"calc(100vh - 180px)"}>
                <Box sx={{ flexGrow: 1, display: (focusedView === 1 ? undefined : "none") }} style={{ maxHeight: maxHeight, overflow: 'auto' }}>
                    {architectureview}
                </Box>
                <Button variant="outlined" sx={{ color: "text.primary", backgroundColor: "action.selected", maxWidth: "10px", minWidth: "10px" }} onClick={architectureViewToggleHandeler}>
                    {(focusedView === 1 ? "<" : ">")}
                </Button>
            </Stack>
            <Stack direction="row" minWidth={focusedView === 1 ? mainWidth : focusedView === 2 ? fullwidth : spWidth}
                display={(interfaceview ? "flex" : "none")} marginTop="5" marginRight="5" minHeight={"calc(100vh - 180px)"}>
                <Box sx={{ flexGrow: 1}} style={{ maxHeight: maxHeight, overflow: 'auto' }}>
                    {interfaceview}
                </Box>
                <Button variant="outlined" sx={{
                    color: "text.primary", backgroundColor: "action.selected", maxWidth: "10px", minWidth: "10px",
                    display: (focusedView >= 2 ? undefined : "none")
                }} onClick={interfaceViewToggleHandeler}>
                    {(focusedView === 2 ? "<" : ">")}
                </Button>
            </Stack>
            <Stack direction="row" minWidth={mainWidth} display={(focusedView >= 3 ? "flex" : "none")} marginTop="5" marginRight="5" minHeight={"calc(100vh - 200px)"}>
                <Box sx={{ flexGrow: 1, display: (focusedView >= 3 ? undefined : "none") }} style={{ maxHeight: maxHeight, overflow: 'auto' }}>
                    <Grid container spacing={2} justifyContent='space-between'>
                        <Grid size={2}>
                            <Button fullWidth variant="outlined" onClick={testcaseViewToggleHandeler} disabled={focusedView === 3}>
                                {"Testcases"}
                            </Button>
                        </Grid>
                        <Grid size={2} >
                            <Button fullWidth variant="outlined" onClick={testcaseViewToggleHandeler} disabled={focusedView === 4} >
                                {"Properties"}
                            </Button>
                        </Grid>
                    </Grid>
                    {focusedView === 4 ? propertiesview : testcaseview}
                </Box>
            </Stack>
        </Stack>
    </>
}
